import React from 'react';
import styled from 'styled-components';
import { get, replace, map, trimEnd } from 'lodash';
import moment from 'moment';
import { bool, object, string, func } from 'prop-types';
import AssignPicker from '../../graphql/assignPicker';
import OrderPickerManager from '../../sections/Orders/OrderPickerManager';
import { Text, Row, Column, Divider, Icon, If } from '../../common';
import { formatTime, timeDiffIn } from '../../../utils/misc';
import OrderShippingEstimate from '../../shared/OrderShippingEstimate';
import { FlexContainer } from '../../layouts/Wrapper/Base.components';
import { IconCencoPrime } from './../../common/Icon/IconCencoPrime';

const OrderBodyRow = styled(Row)`
    display: flex;
`;

const CardText = styled(Text)`
    font-size: 12px;
    color: #333333;
`;

const OrderCardBody = ({ data, isDelayed, text, onPickerModalOpen, onModalPickerClose }) => {
    const pickingStartedTs = get(data, 'assignInfo.pickingTime.init');
    const runningPickingTime = pickingStartedTs ? `${formatTime(timeDiffIn(pickingStartedTs))}` : '-';
    const salesChannelId = get(data, 'salesChannel.id', '1');
    const shippingChange = get(data, 'shippingChange', false);
    const { fullName, isVIP, isCencoPrime } = get(data, 'customer', {});
    const getTimeToDelivery = (logisticsInfo) => {
        const deliveryType = trimEnd(get(logisticsInfo, 'selectedSla', null));
        const init = moment(get(logisticsInfo, 'shippingETA'));
        switch (deliveryType) {
            case 'Despacho a Domicilio':
            case 'Despacho Especial':
            case 'Despacho Especial 1000':
            case 'Despacho Especial 1500':
                const end = moment(get(logisticsInfo, 'shippingETAend'));
                const diff = moment(end.diff(init, 'seconds'));
                const result = init.add(diff / 2, 'seconds').format('YYYY-MM-DD HH:mm:ss');
                return moment(result, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
            case 'Click al Auto':
            case 'Retiro en Tienda':
                return moment(init, 'YYYY-MM-DD HH:mm:ss').format('HH:mm');
            default:
                break;
        }
    };

    const clientEmblems = [
        {
            condtion: isVIP,
            then: <Icon name='vip' size={20} right={5} color='#4DB457' />
        },
        {
            condtion: isCencoPrime && isVIP,
            then: <IconCencoPrime />
        },
        {
            condition: true,
            then: fullName
        }
    ];

    return (
        <>
            <Divider />
            <FlexContainer justify='space-between'>
                <OrderShippingEstimate {...data}>
                    {({ deliveryTime, deliveryDate }) => map(['Fecha de entrega', deliveryDate], el =>
                        <Text color={shippingChange ? '#5665f5' : '#848484'} strong={shippingChange} size={10} text={el} />
                    )}
                </OrderShippingEstimate>
            </FlexContainer>
            <Row style={{ textAlign: 'center' }}>
                <Column align='center'>
                    <Text text={moment(get(data, 'assignInfo.startTime')).format('HH:mm')} strong size={15} />
                    <Text size={10} text='Inicio picking' />
                </Column>
                <Column align='center' justify='space-between'>
                    <Text text={`${runningPickingTime}`} strong size={15} />
                    <Text size={10} text='Tiempo picking' />
                </Column>
                <Column align='center'>
                    <OrderShippingEstimate {...data}>
                        {({ deliveryTimeHourBySla }) => {
                        const isExpress = trimEnd(get(data, 'logisticsInfo.selectedSla', null)) === 'Despacho Express';
                        const timeToDelivery = (isExpress) ? replace(deliveryTimeHourBySla, ' hrs.', '') : getTimeToDelivery(get(data, 'logisticsInfo'));
                            return <Text text={timeToDelivery} strong size={15} />;
                        }}
                    </OrderShippingEstimate>
                    <Text size={10} text='Hora entrega' />
                </Column>
            </Row>
            <Divider />
            <OrderBodyRow>
                <Column align='flex-start' justify='center'>
                    <CardText text='Cliente' />
                </Column>
                <Column align='flex-end' justify='center'>
                    <Text
                        truncate
                        text={map(clientEmblems, properties => <If {...properties} />)}
                    />
                </Column>
            </OrderBodyRow>
            <Divider />
            <OrderBodyRow>
                <Column align='flex-start' justify='center'>
                    <CardText text='Unidades' />
                </Column>
                <Column align='flex-end' justify='center'>
                    <Text text={get(data, 'itemCount')} strong />
                </Column>
            </OrderBodyRow>
            <Divider />
            <OrderBodyRow>
                <Column align='flex-start' justify='center'>
                    <CardText text='Items' />
                </Column>
                <Column align='flex-end' justify='center'>
                    <Text text={get(data, 'skuCount')} strong />
                </Column>
            </OrderBodyRow>
            <Divider />
            <OrderBodyRow>
                <Column align='flex-start' justify='center'>
                    <CardText text='Comuna' />
                </Column>
                <Column align='flex-end' justify='center'>
                    <Text text={get(data, 'customer.address.neighborhood', 'N/A')} strong />
                </Column>
            </OrderBodyRow>
            <Divider />
            <OrderBodyRow>
                <Column align='flex-start' justify='center'>
                    <CardText text='Shopper' />
                </Column>
                <Column align='flex-end' justify='center'>
                    <AssignPicker>
                        {({ updateFn, loading }) => (
                            <OrderPickerManager
                                salesChannelId={get(data, 'salesChannel.id', null)}
                                permissions={['orders-pending-page:assign-picker']}
                                maxWidth='150px'
                                filters={{
                                    filters: [
                                        { field: 'salesChannels', value: [salesChannelId] },
                                        { field: 'statusUser', value: ['0'] }
                                    ]
                                }}
                                onPickerModalOpen={onPickerModalOpen}
                                onSelectPicker={({ value }) => Promise.resolve(updateFn({ variables: { input: { orderId: get(data, 'id'), userId: value } } }))}
                                loading={loading}
                                pickingInfo={get(data, 'assignInfo', {})}
                                {...data}
                            />
                        )}
                    </AssignPicker>
                </Column>
            </OrderBodyRow>
            <Divider />
        </>
    );
};

OrderCardBody.defaultProps = {
    onPickerModalOpen: () => (null),
    onPickerModalClose: () => (null)
};

OrderCardBody.propTypes = {
    isDelayed: bool,
    data: object,
    text: string,
    onPickerModalOpen: func,
    onPickerModalClose: func
};

export default OrderCardBody;
